import { getCoreRowModel, TableOptions, useReactTable } from '@tanstack/react-table';
import { ComponentProps, ReactNode, useMemo } from 'react';

import { BaseEntityTableGroupKey, EntityTable, EntityTableGroupBy } from '@/components/table/entity-table';
import { AdminSession } from '@airreading/common/data';
import {
  futureSessionsPageColumns,
  overlappingSessionsPageColumns,
  pastSessionsPageColumns,
  sessionsPageColumns,
  SessionPageColumns,
  subSessionsPageColumns,
  teacherFutureSessionsPageColumns,
  teacherPastSessionsPageColumns,
  todaySessionsPageColumns,
} from './sessions-page-columns';

export type EntityTableProps = ComponentProps<typeof EntityTable>;

export type SessionsTableFrom =
  | 'future'
  | 'past'
  | 'today'
  | 'sub'
  | 'overlapping'
  | 'school'
  | 'teacher-future'
  | 'teacher-past';

const SESSIONS_TABLE_PREFIX: Partial<Record<SessionsTableFrom, string>> = {
  today: 'sessions-today',
  future: 'sessions-future',
  past: 'sessions-past',
  sub: 'sessions-sub',
  overlapping: 'sessions-overlapping',
};

export interface SessionsTableProps<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey> {
  sessions: AdminSession[];
  errorState?: ReactNode;
  isLoading?: boolean;
  hasError?: boolean;
  filters?: EntityTableProps['filters'];
  tableHookProps?: Omit<TableOptions<AdminSession>, 'columns' | 'data' | 'getCoreRowModel'>;
  displayTimeZone: string;
  bulkActionsMenuItems?: ReactNode;
  topBarRightComponents?: ReactNode;
  paginationOptions?: number[];
  from?: SessionsTableFrom;
  groupBy?: EntityTableGroupBy<AdminSession, GroupKey>;
}

export function SessionsTable<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey>({
  sessions,
  errorState,
  isLoading,
  hasError,
  filters,
  tableHookProps,
  displayTimeZone,
  bulkActionsMenuItems,
  topBarRightComponents,
  from,
  paginationOptions,
  groupBy,
}: SessionsTableProps<GroupKey>) {
  const tablePrefix = from ? SESSIONS_TABLE_PREFIX[from] : undefined;

  const columns = useMemo(() => {
    switch (from) {
      case 'future':
        return futureSessionsPageColumns(displayTimeZone, tablePrefix);
      case 'today':
        return todaySessionsPageColumns(displayTimeZone, undefined, tablePrefix);
      case 'sub':
        return subSessionsPageColumns(displayTimeZone);
      case 'overlapping':
        return overlappingSessionsPageColumns(displayTimeZone);
      case 'past':
        return pastSessionsPageColumns(displayTimeZone);
      case 'teacher-future':
        return teacherFutureSessionsPageColumns(displayTimeZone);
      case 'teacher-past':
        return teacherPastSessionsPageColumns(displayTimeZone);
      default:
        return sessionsPageColumns(displayTimeZone, from);
    }
  }, [displayTimeZone, from, tablePrefix]);

  const table = useReactTable({
    data: sessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    ...tableHookProps,
    defaultColumn: {
      enableSorting: false,
      ...tableHookProps?.defaultColumn,
    },
    state: {
      ...tableHookProps?.state,
    },
  });

  return (
    <EntityTable
      table={table}
      emptyState={'No sessions found'}
      errorState={errorState}
      hasError={hasError}
      isLoading={isLoading}
      filters={filters}
      showPagination={true}
      bulkActionsMenuItems={bulkActionsMenuItems}
      topBarRightComponents={topBarRightComponents}
      paginationOptions={paginationOptions}
      headerClassName={from === 'today' ? 'px-2 py-0 m-0' : undefined}
      rowClassName={from === 'today' ? 'px-2 py-0 m-0' : undefined}
      emptyStateClassName="translate-y-0"
      groupBy={groupBy}
      bulkActionsTestId={tablePrefix ? `${tablePrefix}-table-button-bulk-actions` : undefined}
      autoManagedColumnIds={['selection', SessionPageColumns.editSession]}
    />
  );
}
